import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IDateRange, IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {WB_CurrencyRow} from "../PA.Core/PAdbSchemaRows";
import {IwbCurrency} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Currency, esWB_Loader_Source,
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {IHaveTags} from "../PA.Core/PA.Core.IF.Aspects";
import {BehaviorSubject} from "rxjs";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {EventBO} from "../PA.TPM.BusinessObjects/EventBO";
import {CurrencyBOValidations} from "./CurrencyBO.Validations";

export class CurrencyBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Currency: WB_CurrencyRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Currency'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Currency = null;
    }

    get WB_Currency(): WB_CurrencyRow {
        return this.drWB_Currency;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        self.LoadProgressProvider.start("Creating Currency");
        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                // "Event already loaded.";
                resolve(self);
                return;
            }
            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Currency.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            self.dbContext.createRecordWithKey(esWB_Currency)
                .then((newCurrencyRow: WB_CurrencyRow) => {
                    newCurrencyRow.setDefaults()
                    self.drWB_Currency = newCurrencyRow;
                    self.dbContext.add(esWB_Currency, newCurrencyRow)
                    self.Identity.setAll(self.WB_Currency.cif.WB_CURRENCY_ID, self.WB_Currency.cif.CURRENCY_ID, self.WB_Currency.cif.CURRENCY_NAME);
                    self.Identity.listenToRecordUpdates(self.WB_Currency, 'WB_CURRENCY_ID', 'CURRENCY_ID', 'CURRENCY_NAME')

                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Currency already loaded.");
                self._OnLoadComplete = null;
                resolve(self);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Currency.")
                return;
            }


            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = CurrencyBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = CurrencyBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aCurrencyRow: any = recordResolver.get(esWB_Currency, anId)

                    if (_.isEmpty(aCurrencyRow)) {
                        error("Currency not found");
                        return;
                    }

                    self.drWB_Currency = aCurrencyRow;
                    self.Identity.setAll(self.WB_Currency.cif.WB_CURRENCY_ID, self.WB_Currency.cif.CURRENCY_ID, self.WB_Currency.cif.CURRENCY_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Currency, 'WB_CURRENCY_ID', 'CURRENCY_ID', 'CURRENCY_NAME')

                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_Currency.cif.CURRENCY_NAME + "(" + self.WB_Currency.cif.CURRENCY_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext for to Validate Currency.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            CurrencyBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })

        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        var wbCurrency = <IwbCurrency>self.drWB_Currency;
        self.SaveProgressProvider.start("Saving " + wbCurrency.CURRENCY_NAME + "(" + wbCurrency.CURRENCY_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Currency.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            let bDone = false;

            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }
                }).then((ignore)=>{
                if (bDone) return
                      return  saveFunction(esWB_Currency, self.drWB_Currency, {})
                            .then((ignored) => {
                                if (bDone) return;

                                if (bBatch) {
                                    self.dbContext.addBatchCompleteListener(() => {
                                        self._bIsSaved = true;
                                    })
                                } else {
                                    self._bIsSaved = true;
                                }
                                self.SaveProgressProvider.finish();
                                resolve(self);
                            })
                            .catch((err) => {
                                self.SaveProgressProvider.exception(err);
                                resolve(self)
                            })
            })

        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }


    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbCurrency = <IwbCurrency>self.drWB_Currency;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbCurrency.CURRENCY_NAME + "(" + wbCurrency.WB_CURRENCY_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Currency

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Currency.markDeleted()


            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const currency_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, currency_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Currency, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }
}

PAEnvironment.registerBusinessObjectClass(CurrencyBO, "CurrencyBO", "Currency")